import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Bell, Clock } from 'lucide-react';

export default function TestNotifications() {
  const [creating, setCreating] = useState(false);

  const createTestReminder = async () => {
    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Calculate time 3 minutes from now
      const now = new Date();
      const in3Minutes = new Date(now.getTime() + 3 * 60 * 1000);
      const reminderTime = in3Minutes.toTimeString().substring(0, 5); // HH:MM format

      // Create test reminder
      const { data, error } = await supabase
        .from('reminders')
        .insert({
          user_id: user.id,
          title: 'Test Reminder - Notification Flow',
          description: 'This is a test reminder to verify the full notification delivery system works correctly.',
          reminder_type: 'custom',
          reminder_time: reminderTime,
          frequency: 'once',
          enabled: true,
          reminder_date: in3Minutes.toISOString().split('T')[0]
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(
        `Test reminder created! Will fire at ${in3Minutes.toLocaleTimeString()}`,
        {
          description: 'Check /notifications in ~3 minutes to see the result'
        }
      );
    } catch (error) {
      console.error('Error creating test reminder:', error);
      toast.error('Failed to create test reminder');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Test Notification System
          </CardTitle>
          <CardDescription>
            Create a test reminder that will fire in 3 minutes to verify the full notification delivery flow
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-muted rounded-lg space-y-2 text-on-light">
            <h3 className="font-medium">What will happen:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>A reminder is created in the database with time set to 3 minutes from now</li>
              <li>The cron job (runs every 10 minutes) will detect this reminder</li>
              <li>It checks your notification preferences and quiet hours</li>
              <li>Sends an in-app notification to the notifications table</li>
              <li>If you have push tokens configured, sends a push notification</li>
              <li>Updates the reminder's last_sent_at timestamp</li>
              <li>You can view the notification in /notifications</li>
            </ol>
          </div>

          <div className="p-4 border border-primary/20 bg-primary/5 rounded-lg">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-primary mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground">Note about timing:</p>
                <p className="text-foreground/80 mt-1">
                  The cron job runs every 10 minutes, so the notification may arrive within 0-10 minutes 
                  of the scheduled time. For immediate testing, you can manually trigger the edge function.
                </p>
              </div>
            </div>
          </div>

          <Button 
            onClick={createTestReminder} 
            disabled={creating}
            className="w-full"
          >
            {creating ? 'Creating Test Reminder...' : 'Create 3-Minute Test Reminder'}
          </Button>

          <div className="text-xs text-foreground/70 space-y-1">
            <p className="text-foreground"><strong>Manual trigger (optional):</strong></p>
            <code className="block p-2 bg-muted rounded text-xs text-foreground/90">
              curl -X POST https://jbdsdzmyqkfbqijnoqmo.supabase.co/functions/v1/deliver-reminders \
              -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpiZHNkem15cWtmYnFpam5vcW1vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTgxMDE5MTUsImV4cCI6MjA3MzY3NzkxNX0.t784M5dFHYwtzERYIifvfrTEUyXaXaCqxHPgdfGc4KY"
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
